import {AfterViewInit, Component, HostBinding, Inject, Input, OnChanges, OnDestroy, OnInit, ViewChild} from '@angular/core';
import moment from 'moment';
import DataSource from 'devextreme/data/data_source';
import {DxDataGridComponent} from 'devextreme-angular';
import {GoldenLayoutContainer} from 'ngx-golden-layout';
import GoldenLayout from 'golden-layout';
import {Taggable, Trailer, TransportIntervention, User} from 'shared';
import {TransportInterventionService} from '../../stores/transport-intervention.service';
import {Subject, Subscription} from 'rxjs';
import {durationToStr, durationToHoursStr} from 'shared';
import tagsIcon from '@iconify/icons-mdi/id-card';
import reportIcon from '@iconify/icons-mdi/chart-areaspline';
import {TransportInterventionsPageRequest} from '../transport-interventions-page/transport-interventions-page-request';
import {TransportInterventionsNavigationService} from '../transport-interventions-navigation/transport-interventions-navigation.service';
import {TransportInterventionsNavigationActivatedComponent} from '../transport-interventions-navigation/transport-interventions-navigation-activated-component';
import {find} from 'lodash';
import {Workbook} from 'exceljs';
import {saveAs} from 'file-saver';
import {exportDataGrid} from 'devextreme/excel_exporter';

export class TransportInterventionsRequest {
  from: Date;
  to: Date;
  isToday: boolean;
  currentFilter = 2;

  constructor() {
    this.from = moment().startOf('day').toDate();
    this.to = moment().endOf('day').toDate();
    this.isToday = true;
  }
}

@Component({
  selector: 'app-transport-interventions-grid',
  templateUrl: './transport-interventions-grid.component.html',
  styleUrls: ['./transport-interventions-grid.component.scss']
})
export class TransportInterventionsGridComponent extends TransportInterventionsNavigationActivatedComponent implements OnInit, OnDestroy, OnChanges, OnDestroy, AfterViewInit {
  ds: DataSource;
  canEdit: boolean;
  canAdd: boolean;
  canDelete: boolean;
  tagsIcon = tagsIcon;
  reportIcon = reportIcon;
  current: TransportIntervention;
  tagDetailsEl: HTMLElement;
  tagDetailsVisible: boolean;
  reportVisible: boolean;

  @Input()
  request: TransportInterventionsPageRequest;

  @Input()
  width: number;

  @Input()
  height: number;

  @Input()
  @HostBinding('hidden')
  hidden: boolean;
  private submitSub: Subscription;
  private hasPendingRequest: boolean;

  @ViewChild(DxDataGridComponent, {static: true})
  grid: DxDataGridComponent;
  gridCss: { width: string; height: string };
  dateFormat = 'shortDateShortTime';
  attributesNames: string[] = [];
  attributesValues: { [key: string]: { text: string, value: string | [] } } = {};

  constructor(
    private transportInterventionService: TransportInterventionService,
    @Inject(GoldenLayoutContainer) private container: GoldenLayout.Container,
    public user: User,
  ) {
    super();
  }

  ngOnInit() {
    // Dims
    this.gridCss = {
      width: this.width + 'px',
      height: this.height - 51 + 'px',
    };
  }

  showTags(e: MouseEvent, data: TransportIntervention) {
    e.preventDefault();
    this.current = data;
    this.tagDetailsEl = e.target as HTMLElement;
    this.tagDetailsVisible = true;
  }

  durationToHoursStr(cellInfo) {
    return durationToHoursStr(cellInfo.value);
  }

  durationToStr(cellInfo) {
    return durationToStr(cellInfo.value);
  }

  showInterventionDetails(cell: any, event?: MouseEvent) {
    if (event) {
      event.preventDefault();
    }
    this.current = cell.hasOwnProperty('data') ? cell.data : cell.row.data;
    this.reportVisible = true;
  }

  private loadRequest() {
    if (!this.ds) {
      // Data source
      this.ds = this.transportInterventionService.newDataSource({
        onLoadingChanged: (isLoading) => {
          setTimeout(() => {
            this.request.isLoading = isLoading;
          }, 10);
        }
      });
      const store = this.ds.store();
      store.on('loaded', (result) => {
        this.attributesNames = result.custom.attributes;
        if (result.custom.attributes_values) {
          const keys = Object.keys(result.custom.attributes_values);
          keys.forEach(k => {
            this.attributesValues[k] = result.custom.attributes_values[k].map(i => {
              return {text: i, value: i};
            });
          });
        }
      });
    }
    this.dateFormat = this.request.isToday ? 'shortTime' : 'shortDateShortTime';

    // Default filter
    this.ds.filter([
      ['start_time', '>=', this.request.from],
      'and',
      ['start_time', '<=', this.request.to]
    ]);

    this.ds.reload();

    this.hasPendingRequest = false;
  }

  onActivated() {
    if (this.hasPendingRequest) {
      setTimeout(() => {
        this.loadRequest();
      }, 10);
    }
  }

  onDeactivated() {
  }

  ngOnDestroy(): void {
    this.submitSub.unsubscribe();
  }

  getTagAttrValue(cell): any {
    const item = cell.data as TransportIntervention;
    const name = cell.column.caption;
    const attr = find(item.tag_details.attributes, {name});
    return attr ? attr.value : '-';
  }

  ngAfterViewInit(): void {
    this.submitSub = this.request.submit$.subscribe(() => {
      if (this.isActivated) {
        setTimeout(() => {
          this.loadRequest();
        }, 10);
      } else {
        this.hasPendingRequest = true;
      }
    });
  }

  onExporting(e) {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Interventions Transport');

    exportDataGrid({
      component: e.component,
      worksheet,
      topLeftCell: {row: 2, column: 2},
      customizeCell: ({gridCell, excelCell}) => {

        if (gridCell.rowType === 'data') {
          if (gridCell.column.dataField.startsWith('attributes.')) {
            excelCell.value = this.getTagAttrValue(gridCell);
          }
        }
      }
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer: BlobPart) => {
        saveAs(new Blob([buffer], {type: 'application/octet-stream'}), 'liste-interventions-transport.xlsx');
      });
    });
    e.cancel = true;
  }
}
